import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LandingPage from './components/Landing/LandingPage';
import StudioWizard from './components/Onboarding/StudioWizard';
import DashboardLayout from './components/Dashboard/DashboardLayout';
import Overview from './components/Dashboard/Overview';
import CoursesPage from './components/Dashboard/CoursesPage';
import StudentsPage from './components/Dashboard/StudentsPage';
import CalendarPage from './components/Dashboard/CalendarPage';
import ClassroomPage from './components/Dashboard/ClassroomPage';
import PaymentsPage from './components/Dashboard/PaymentsPage';
import NotificationsSettings from './components/Dashboard/NotificationsSettings';
import SettingsPage from './components/Dashboard/SettingsPage';
import StudioPublicPage from './components/Studio/StudioPublicPage';
import StudentGuidePage from './components/Studio/StudentGuidePage';
import EnrollmentPage from './components/Enrollment/EnrollmentPage';
import EnrollmentSuccess from './components/Enrollment/EnrollmentSuccess';
import StudentDashboardLayout from './components/StudentDashboard/StudentDashboardLayout';
import StudentOverview from './components/StudentDashboard/StudentOverview';
import MyCourses from './components/StudentDashboard/MyCourses';
import CourseDetail from './components/StudentDashboard/CourseDetail';
import StudentCalendar from './components/StudentDashboard/StudentCalendar';
import MaterialsHub from './components/StudentDashboard/MaterialsHub';
import StudentClassroom from './components/StudentDashboard/StudentClassroom';
import MyPayments from './components/StudentDashboard/MyPayments';
import StudentProfile from './components/StudentDashboard/StudentProfile';
import QRScanPage from './components/StudentDashboard/QRScanPage';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return isAuthenticated ? children : <Navigate to="/" />;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-gray-400 text-sm">Loading...</div>
  </div>
);

const NoEnrollment = () => {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Courses Yet</h2>
        <p className="text-gray-500 mb-6">
          You haven't enrolled in any courses yet. Once you enroll in a course through a studio, your dashboard will appear here.
        </p>
        <button
          onClick={logout}
          className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
        >
          Log out
        </button>
      </div>
    </div>
  );
};

const RootRedirect = () => {
  const { isAuthenticated, user, studio, loading } = useAuth();

  if (!isAuthenticated) return <LandingPage />;

  // Wait for user data before routing
  if (loading || !user) return <LoadingScreen />;

  if (user.role === 'student') {
    return user.studioId ? <Navigate to="/student" /> : <NoEnrollment />;
  }

  // Teacher (default)
  return studio ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding/studio" />;
};

const App = () => {
  const { loading } = useAuth();

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<RootRedirect />} />

      {/* Onboarding (teachers only) */}
      <Route
        path="/onboarding/studio"
        element={
          <ProtectedRoute>
            <OnboardingGuard />
          </ProtectedRoute>
        }
      />

      {/* Teacher Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <TeacherDashboardGuard />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/new" element={<CoursesPage creating />} />
        <Route path="materials" element={<Navigate to="/dashboard/students?tab=materials" />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="attendance" element={<Navigate to="/dashboard/students?tab=attendance" />} />
        <Route path="classroom" element={<ClassroomPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="notifications" element={<NotificationsSettings />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<Navigate to="/dashboard/settings" />} />
      </Route>

      {/* Student Dashboard */}
      <Route
        path="/student"
        element={
          <ProtectedRoute>
            <StudentDashboardGuard />
          </ProtectedRoute>
        }
      >
        <Route index element={<StudentOverview />} />
        <Route path="courses" element={<MyCourses />} />
        <Route path="course/:courseId" element={<CourseDetail />} />
        <Route path="calendar" element={<StudentCalendar />} />
        <Route path="materials" element={<MaterialsHub />} />
        <Route path="classroom" element={<StudentClassroom />} />
        <Route path="payments" element={<MyPayments />} />
        <Route path="scan" element={<QRScanPage />} />
        <Route path="profile" element={<StudentProfile />} />
      </Route>

      {/* Public studio page + enrollment */}
      <Route path="/s/:slug" element={<StudioPublicPage />} />
      <Route path="/s/:slug/guide" element={<StudentGuidePage />} />
      <Route path="/s/:slug/enroll/:courseId" element={<EnrollmentPage />} />

      {/* Enrollment success */}
      <Route
        path="/enrollment/success"
        element={
          <ProtectedRoute>
            <EnrollmentSuccess />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Guard: only teachers without a studio see onboarding; others redirect
const OnboardingGuard = () => {
  const { user, studio } = useAuth();

  if (user?.role === 'student') return <Navigate to="/student" />;
  return studio ? <Navigate to="/dashboard" /> : <StudioWizard />;
};

// Guard: only teachers with a studio see the dashboard
const TeacherDashboardGuard = () => {
  const { user, studio } = useAuth();

  if (user?.role === 'student') return <Navigate to="/student" />;
  return studio ? <DashboardLayout /> : <Navigate to="/onboarding/studio" />;
};

// Guard: only students see the student dashboard
const StudentDashboardGuard = () => {
  const { user } = useAuth();

  if (user?.role === 'teacher') return <Navigate to="/dashboard" />;
  return user?.studioId ? <StudentDashboardLayout /> : <Navigate to="/" />;
};

export default App;
